import {
  Checkbox,
  FieldErrors,
  Input,
  InputMasked,
  Maintenance,
  Phone,
  Select,
} from '@chiroup/components';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import { SORTED_SCHOOLS } from '@chiroup/core/constants/schools';
import { COUNTRIES } from '../../constants';
import { EmailValidator, useForm } from '@chiroup/hooks';
import { EyeIcon } from '@heroicons/react/24/solid';
import qs from 'query-string';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DarkDashboard from '../../images/chiroup-dark.jpg';
import LightDashboard from '../../images/chiroup-light.jpg';
import referralService from '../../services/referral.service';
import userService from '../../services/user.service';
import Alert, { AlertIcons } from '../common/Alert';
import Button, { ButtonColors } from '../common/Button';
import Uploader from '../common/uploader/Uploader';
import { PasswordValidator } from '../common/validators/passwordValidator';
import ValidEmailAlert from '../common/ValidEmailAlert';
import Logo from '../layout/Logo';
import PasswordValidationMeter from './PasswordValidationMeter';
import { signIn, signUp } from '@aws-amplify/auth';

type SignUpForm = {
  clinicName: string;
  email: string;
  fname: string;
  lname: string;
  password: string;
  referralCode: string;
  recaptcha: string;
  plan: string;
  schoolName: string;
  graduationDate: string;
  phone: string;
  idImage: string | null;
  termsOfUse: boolean;
  accountType: string;
  country: string;
};

type ReferralInfo = {
  basic: number;
  duration: number;
  image: string;
  message: string;
  plus: number;
  premium: number;
  logo: string;
  [key: string]: string | number;
};

type Props = {
  setAppLoading: Dispatch<SetStateAction<boolean>>;
  theme: 'light' | 'dark';
};

const SignUp: React.FC<Props> = ({ setAppLoading, theme }) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const recaptchaRefStepOne = useRef<ReCAPTCHA>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { search } = useLocation();
  const [hasReferralCode, setHasReferralCode] = useState(false);
  const [downForMaintenance, setDownForMaintenance] = useState<{
    title: string;
    message: string;
  } | null>();
  const [isPasswordVisible, setPasswordVisibility] = useState(false);
  const {
    value: valueOne,
    registerSubmit: registerSubmitStepOne,
    errors: errorsStepOne,
    isSubmitting: submittingStepOne,
    onChange: onChangeStepOne,
  } = useForm<Partial<SignUpForm>>(
    { email: '' },
    {
      email: {
        required: {
          message: 'Email is required.',
        },
        pattern: EmailValidator,
      },
      fname: {
        required: {
          message: 'First name is required.',
        },
      },
      lname: {
        required: {
          message: 'Last name is required.',
        },
      },
    },
  );
  const { value, registerSubmit, onChange, errors, isSubmitting, patchValue } =
    useForm<Partial<SignUpForm>>(
      {
        clinicName: '',
        country: '',
        password: '',
        referralCode: '',
        plan: 'trial',
        schoolName: '',
        graduationDate: '',
        phone: '',
        termsOfUse: false,
        accountType: 'provider',
        idImage: '',
      },
      {
        clinicName: {
          function: {
            value: (val) => {
              if (!education && !val.clinicName) {
                return 'Clinic name is required.';
              }
              return false;
            },
          },
        },
        country: {
          required: {
            message: 'Country is required.',
          },
        },
        password: {
          required: {
            message: 'Password is required.',
          },
          pattern: PasswordValidator,
        },
        // recaptcha: {
        //   required: {
        //     message: 'Please click the box above.',
        //   },
        // },
        schoolName: {
          function: {
            value: (val) => {
              if (education && !val.schoolName) {
                return 'School name is required.';
              }
              return false;
            },
          },
        },
        graduationDate: {
          function: {
            value: (val) => {
              if (val.accountType === 'student' && !val.graduationDate) {
                return 'Graduation date is required.';
              }
              if (
                val.accountType === 'student' &&
                (val.graduationDate || '').length < 7
              ) {
                return 'Graduation date must be completed in the form MM/YYYY.';
              }
              return false;
            },
          },
        },
        phone: {
          function: {
            value: (val) => {
              const { phone } = val;
              if (education && !val.phone) {
                return 'Phone is required.';
              } else if (
                phone?.[1] === '1' &&
                phone?.split(' ')?.[0]?.length === 6 &&
                phone?.length < 13
              ) {
                return 'Phone number must be 7 digits.';
              } else if (phone?.[1] === '1' && phone?.length < 13) {
                return 'Phone number must be 10 digits.';
              }
              return false;
            },
          },
        },
        idImage: {
          function: {
            value: (val) => {
              if (education && !val.idImage) {
                return 'College ID is required.';
              }
              return false;
            },
          },
        },
        termsOfUse: {
          function: {
            value: (val) => {
              if (!val.termsOfUse) {
                return 'You must accept the terms of use';
              }
              return false;
            },
          },
        },
      },
    );

  const [emailExists, setEmailExists] = useState(false);
  const [serverErrors, setServerErrors] = useState<FormError>();
  const [education, setEducation] = useState(false);
  const [qsPlan, setQsPlan] = useState<string>();
  const [ReferralCodeInfo, setReferralCodeInfo] =
    useState<ReferralInfo | null>();
  const [step, setStep] = useState<number>(1);
  const [isCheckingEmail, setIsCheckingEmail] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<any>();
  const [notValidEmail, setNotValidEmail] = useState<boolean>(false);
  const [emailJustChecked, setEmailJustChecked] = useState<string>('');

  useEffect(() => {
    const queryParams = qs.parse(search);
    const plan = queryParams.plan as string;
    const refer = queryParams.refer as string;
    if (plan) {
      patchValue({ plan });
      setQsPlan(plan);
      if (plan === 'education') {
        setEducation(true);
      }
    }
    if (refer) {
      setHasReferralCode(true);
      checkReferralCode(refer);
      patchValue({ referralCode: refer });
    }
  }, [search, patchValue]);

  useEffect(() => {
    if (!valueOne.email) {
      setEmailExists(false);
    }
  }, [valueOne.email]);

  const checkEmailForEd = useCallback(() => {
    const lastThree = valueOne?.email?.slice(-3);
    if (lastThree === 'edu') {
      return true;
    }
    return false;
  }, [valueOne?.email]);

  // useEffect(() => {
  //   if (education && !checkEmailForEd()) {
  //     setEdEmailError({
  //       message: 'Email address with .edu required for education account',
  //     });
  //   }
  // }, [checkEmailForEd, education]);

  const onSubmit = async (val: any) => {
    let captchaTokenRes = captchaToken;
    // setEdEmailError(undefined);
    setEmailExists(false);
    setServerErrors(undefined);
    if (!captchaToken) {
      captchaTokenRes = await recaptchaRef.current?.executeAsync();
      setCaptchaToken(captchaTokenRes);
      if (!captchaTokenRes) {
        return;
      }
    }
    if (!valueOne.email || !value.password) {
      return;
    }
    // if (!confirmEd && !education) {
    //   const isEdEmail = checkEmailForEd();
    //   if (isEdEmail) {
    //     setConfirmEd(true);
    //     throw new Error('edu');
    //   }
    // }
    const username = valueOne.email;
    const password = value.password;
    const attributes: any = {
      'custom:captcha': captchaTokenRes,
      given_name: valueOne.fname,
      family_name: valueOne.lname,
      'custom:accountType': value.accountType,
      'custom:country': value.country,
    };
    if (education) {
      attributes['custom:schoolName'] = value.schoolName;
      attributes['custom:graduationDate'] = value.graduationDate;
      attributes['custom:phone'] = value.phone;
      attributes.phone_number = (value.phone || '').replace(/ /g, '');
      attributes['custom:idImage'] = value.idImage;
    } else {
      attributes['custom:clinicName'] = value.clinicName;
      attributes['custom:plan'] = value.plan;
    }
    if (hasReferralCode) {
      attributes['custom:referralCode'] = value.referralCode;
      attributes['custom:referralSource'] =
        (ReferralCodeInfo as any)?.coupon?.metadata?.source || '';
    }
    if (value.termsOfUse) {
      attributes['custom:termsOfUse'] = '1';
    }
    return signUp({
      username,
      password,
      options: {
        userAttributes: attributes,
      },
    }) as any;
  };

  const onFail = async (err: any) => {
    console.error('inside on Fail', err);
    if (err?.response?.data?.statusCode === 503) {
      setDownForMaintenance(err?.response?.data);
    }
    if (err?.message === 'edu') {
      return;
    }
    if (err?.message === 'PreSignUp failed with error MIGRATE.') {
      navigate('/migration');
    }
    if (err?.message === 'An account with the given email already exists.') {
      return setEmailExists(true);
    }
    const message = err?.message || '';
    setServerErrors({
      message: message.replace(/PreSignUp failed with error /g, ''),
    });
  };

  const onSuccess = async () => {
    if (!valueOne.email || !value.password) {
      return;
    }
    setAppLoading(true);
    await signIn({ username: valueOne.email, password: value.password });
    setAppLoading(false);
    if (qsPlan) {
      return navigate('/', { state: { qsPlan } });
    } else {
      return navigate({
        pathname: '/',
      }) as any;
    }
  };

  const checkReferralCode = async (code: string) => {
    const res = await referralService.getReferralInfo(code);
    if (res) {
      setReferralCodeInfo(res[0]);
    } else {
      setReferralCodeInfo(null);
    }
  };

  const stepForward = async () => {
    setIsCheckingEmail(true);
    setEmailExists(false);
    if (emailJustChecked !== valueOne.email) {
      const zbResult = await userService.checkIfValidEmail(
        valueOne.email || '',
      );
      setEmailJustChecked(valueOne.email || '');
      if (zbResult?.data?.status?.toLowerCase() === 'invalid') {
        setNotValidEmail(true);
        setIsCheckingEmail(false);
        throw new Error('invalid_email');
      }
    }
    const userExists = await userService
      .checkExists(valueOne.email || '', String(document.referrer || ''), {
        fname: valueOne.fname,
        lname: valueOne.lname,
      })
      .catch((err) => {
        if (err?.response?.data?.statusCode === 503) {
          setDownForMaintenance(
            err?.response?.data?.message ||
              'Sorry for the inconvenience, we are currently down for maintenance.',
          );
        }
      });
    //check email for .edu?
    if (userExists) {
      setIsCheckingEmail(false);
      setEmailExists(true);
      return;
    } else {
      setIsCheckingEmail(false);
      onSuccessStepOne();
    }
  };

  const onFailStepOne = () => {
    setIsCheckingEmail(false);
  };

  const onSuccessStepOne = () => {
    if (valueOne.email && valueOne.fname && valueOne.lname) {
      const isEd = checkEmailForEd();
      if (isEd) {
        setEducation(true);
        patchValue({ plan: 'education', accountType: 'student' });
      }
      setIsCheckingEmail(false);
      setStep(2);
    }
  };

  // const applyReferralCode = async () => {
  //   try {
  //     setReferralCodeInfo(null);
  //     setApplyingReferralCode(true);
  //     const res = await ChiroUpAPI.post('api-noauth', `/billing/promo-code`, {
  //       body: { code: value.referralCode },
  //     });
  //     setApplyingReferralCode(false);
  //     setReferralCodeInfo(res?.code || res || { notFound: true });
  //     return res?.code || false;
  //   } catch (err) {
  //     setApplyingReferralCode(false);
  //     createToast({
  //       title: 'Referral code error',
  //       description: <span>Failed to check referral code.</span>,
  //       type: ToastTypes.Fail,
  //     });
  //     return false;
  //   }
  // };

  if (downForMaintenance) {
    return (
      <Maintenance
        title={downForMaintenance.title}
        message={downForMaintenance.message}
      />
    );
  }

  return (
    <div>
      <div className="sm:hidden">
        <Logo className="mt-8 ml-4" />
      </div>
      <div
        className="grid h-screen place-items-center bg-cover bg-left-top"
        style={
          step === 2
            ? {
                backgroundImage: `url(${
                  theme === 'dark' ? DarkDashboard : LightDashboard
                })`,
              }
            : { backgroundImage: 'none' }
        }
      >
        <div className="sm:box-border sm:border-12 sm:border-gray-300 sm:rounded-3xl flex justify-center h-screen sm:w-2/3 sm:h-fit max-w-3xl w-full bg-white dark:bg-gray-800">
          <div className="flex flex-row flex-auto">
            <div className="sm:w-2/3 sm:pt-10 sm:pl-10 sm:pr-10 pt-5 pl-5 pr-5">
              <div className="sm:text-center leading-4">
                <p className="text-primary-500 lg:text-3xl font-medium sm:text-xl mb-4 text-left text-2xl leading-6 w-screen sm:w-auto">
                  {step === 1 ? 'Welcome to ChiroUp.' : "You're almost in!"}
                </p>
                <p className="text-left mb-6 lg:text-lg text-sm">
                  {step === 1
                    ? "Get started - it's free. No credit card required."
                    : 'Just a few more details to create your account.'}
                </p>
              </div>

              {step === 1 && (
                <form
                  className="mt-2"
                  onSubmit={registerSubmitStepOne(stepForward, {
                    onFail: onFailStepOne,
                  })}
                >
                  <div className="h-full">
                    <Input
                      label="Email*"
                      name="email"
                      onChange={onChangeStepOne('email')}
                      value={valueOne.email}
                      errors={errorsStepOne.fieldErrors?.email}
                      autoFocus
                      className="flex-grow mb-2"
                    />
                    {valueOne.email && emailExists && (
                      <Alert className="mt-2" icon={AlertIcons.info}>
                        It looks like you already have an account with ChiroUp.{' '}
                        <Link
                          to="/"
                          className="font-medium underline text-blue-700 hover:text-blue-600"
                        >
                          Click here to login.
                        </Link>
                      </Alert>
                    )}
                    {notValidEmail && <ValidEmailAlert styles={'mt-2'} />}
                    <Input
                      label="First name*"
                      name="fname"
                      onChange={onChangeStepOne('fname')}
                      value={valueOne.fname}
                      errors={errorsStepOne.fieldErrors?.fname}
                      className="flex-grow mb-2"
                    />

                    <Input
                      label="Last name*"
                      name="lname"
                      onChange={onChangeStepOne('lname')}
                      value={valueOne.lname}
                      errors={errorsStepOne.fieldErrors?.lname}
                      className="flex-grow mb-2"
                    />
                    <div className="text-center">
                      <Button
                        text="Continue"
                        type="submit"
                        color={ButtonColors.accent}
                        className="mt-4 text-xl"
                        disabled={isCheckingEmail || submittingStepOne}
                        loading={isCheckingEmail || submittingStepOne}
                      />
                    </div>
                  </div>
                  <div className="my-4">
                    <ReCAPTCHA
                      sitekey="6LfJsiYeAAAAAHvjftd1pX9kzfiWkRKeK-oMJlWM"
                      size="invisible"
                      ref={recaptchaRefStepOne}
                    />
                    <FieldErrors errors={errors.fieldErrors?.recaptcha} />
                  </div>
                </form>
              )}

              {step === 2 && (
                <form
                  className="mt-2"
                  onSubmit={registerSubmit(onSubmit, { onSuccess, onFail })}
                >
                  <div className="w-full flex flex-col gap-2">
                    <Select
                      label="Signing up as*"
                      name="accountType"
                      value={value.accountType}
                      options={[
                        {
                          text: 'Student',
                          value: 'student',
                        },
                        {
                          text: 'Provider',
                          value: 'provider',
                        },
                        {
                          text: 'Faculty',
                          value: 'faculty',
                        },
                        { text: 'Other', value: 'other' },
                      ]}
                      onChange={(val) => {
                        setEducation(val === 'student' || val === 'faculty');
                        patchValue({
                          accountType: val,
                          plan:
                            val === 'student' || val === 'faculty'
                              ? 'education'
                              : 'trial',
                        });
                      }}
                      errors={errors?.fieldErrors?.accountType}
                      className="w-full"
                      limit={1}
                    />
                    {education ? (
                      <>
                        <Select
                          className=""
                          name="schoolName"
                          label="School name *"
                          options={SORTED_SCHOOLS}
                          valueField="name"
                          labelField="name"
                          value={value.schoolName}
                          onChange={onChange('schoolName')}
                          errors={errors?.fieldErrors?.schoolName}
                          limit={1}
                        />
                        {value.accountType === 'student' && (
                          <InputMasked
                            name="graduationDate"
                            label="Graduation date *"
                            value={value.graduationDate}
                            onChange={onChange('graduationDate')}
                            errors={errors?.fieldErrors?.graduationDate}
                            patternFormat="##/####"
                            placeholder="MM/YYYY"
                          />
                        )}
                        <Phone
                          className=""
                          name="phone"
                          label="Phone number *"
                          value={value.phone}
                          onChange={onChange('phone')}
                          errors={errors?.fieldErrors?.phone}
                        />
                        <Uploader
                          name="idImage"
                          label="College ID *"
                          onChange={onChange('idImage')}
                          className="mt-2"
                          value={value.idImage}
                          type="image"
                          uploadCategory="education"
                          uploadType="image"
                          fileTypes={['png', 'jpg', 'jpeg', 'gif']}
                          fileSizeLimit={10}
                          errors={errors?.fieldErrors?.idImage}
                          noPreview
                        />
                      </>
                    ) : (
                      <Input
                        label="Clinic name*"
                        name="clinicName"
                        onChange={onChange('clinicName')}
                        value={value.clinicName}
                        errors={errors.fieldErrors?.clinicName}
                      />
                    )}
                    <Select
                      name="country"
                      label="Country *"
                      className="col-span-12 sm:col-span-6"
                      options={COUNTRIES}
                      value={value.country}
                      onChange={onChange('country')}
                      errors={errors?.fieldErrors?.country}
                      limit={1}
                      native
                    />
                    <Input
                      label="Password*"
                      name="password"
                      onChange={onChange('password')}
                      type={isPasswordVisible ? 'text' : 'password'}
                      value={value.password}
                      errors={errors.fieldErrors?.password || serverErrors}
                      ref={passwordRef}
                      icon={
                        <EyeIcon
                          className="text-gray-300 hover:text-gray-400 h-5 w-5 cursor-pointer"
                          onClick={() => {
                            setPasswordVisibility((prev) => !prev);
                          }}
                        />
                      }
                      clickIcon
                    />
                    {value.password && (
                      <PasswordValidationMeter
                        password={value.password}
                        errors={errors.fieldErrors?.password}
                      />
                    )}
                    {hasReferralCode ? (
                      <div className="text-left flex flex-col gap-4">
                        <Input
                          className="col-span-12"
                          label="Enter your referral code"
                          name="referralCode"
                          onChange={onChange('referralCode')}
                          value={value.referralCode}
                          errors={errors.fieldErrors?.referralCode}
                        />
                      </div>
                    ) : (
                      <div className="text-sm mt-2">
                        <span
                          className="font-medium text-primary-600 hover:text-primary-400 cursor-pointer"
                          onClick={() => setHasReferralCode(true)}
                        >
                          Have a referral code?
                        </span>
                      </div>
                    )}
                    <div className="mt-4">
                      <Checkbox
                        label={
                          <span>
                            I agree to the{' '}
                            <a
                              href={
                                education
                                  ? 'https://help.chiroup.com/en_US/Security-and-privacy/student-faculty-terms-of-use'
                                  : 'https://help.chiroup.com/en_US/Security-and-privacy/terms-of-use'
                              }
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary-500"
                            >
                              terms of use{' '}
                            </a>
                            and{' '}
                            <a
                              href="https://help.chiroup.com/en_US/Security-and-privacy/1136907-view-the-chiroup-privacy-policy"
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary-500"
                            >
                              {' '}
                              privacy policy.
                            </a>
                          </span>
                        }
                        value={value.termsOfUse}
                        onChange={onChange('termsOfUse')}
                        errors={errors.fieldErrors?.termsOfUse}
                      />
                    </div>{' '}
                    <div className="text-center">
                      <Button
                        type="submit"
                        text="Create account"
                        loading={isSubmitting}
                        color={ButtonColors.accent}
                        className="mt-4 text-xl mb-4"
                      />
                    </div>
                    <div className="my-4">
                      <ReCAPTCHA
                        sitekey="6LfJsiYeAAAAAHvjftd1pX9kzfiWkRKeK-oMJlWM"
                        // onChange={onChangeCaptcha}
                        size="invisible"
                        ref={recaptchaRef}
                      />
                      <FieldErrors errors={errors.fieldErrors?.recaptcha} />
                    </div>
                  </div>
                </form>
              )}
              {step === 1 && (
                <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-100 mb-4">
                  Already have an account?
                  <Link
                    to="/"
                    className="font-medium text-primary-500 hover:text-primary-300"
                  >
                    {' '}
                    Log in
                  </Link>
                </p>
              )}
              <div className="mt-4 sm:hidden">
                {step === 1 && (
                  <>
                    <img
                      data-src="https://images.squarespace-cdn.com/content/v1/615dc239b586c31b30e953a1/0056163f-db58-47de-be7d-e6020881a72e/chiroup+chiropractic+software1.png"
                      data-image="https://images.squarespace-cdn.com/content/v1/615dc239b586c31b30e953a1/0056163f-db58-47de-be7d-e6020881a72e/chiroup+chiropractic+software1.png"
                      data-image-dimensions="2353x1440"
                      data-image-focal-point="0.5,0.5"
                      alt=""
                      loading="lazy"
                      data-stretch="true"
                      data-parent-ratio="1.3"
                      data-image-resolution="750w"
                      src="https://images.squarespace-cdn.com/content/v1/615dc239b586c31b30e953a1/0056163f-db58-47de-be7d-e6020881a72e/chiroup+chiropractic+software1.png?format=750w"
                    />
                    <p className="text-sm text-center ml-2 mr-2 leading-1">
                      Trusted by over 2,500 chiropractors around the world.
                    </p>
                    <p className="text-md leading-1 mt-10 text-primary-600">
                      Diagnose &#38; Prescribe Smarter (for 100+ conditions)
                    </p>
                    <p className="italic text-sm leading-1 mt-2 mb-4">
                      Evidence-based best practices at your fingertips.
                    </p>
                    <img
                      alt=""
                      loading="lazy"
                      src="https://images.squarespace-cdn.com/content/v1/615dc239b586c31b30e953a1/c8a37853-29ee-4e26-b3a7-6660b2818918/chiroup_first_gi.gif?format=1000w"
                    ></img>
                    <p className="text-md leading-1 mt-10 text-primary-600">
                      Give Your Patients Something To Talk About
                    </p>
                    <p className="italic text-sm leading-1 mt-2 mb-4">
                      Create highly engaging patient education and rehab YOUR
                      way.
                    </p>
                    <img
                      alt=""
                      src="https://images.squarespace-cdn.com/content/v1/615dc239b586c31b30e953a1/888dccdd-8a97-443b-b769-3deda0d822e0/Update-chiroup.gif?format=750w"
                    />
                    <p className="text-md leading-1 mt-10 text-primary-600">
                      Practice at the Highest Level
                    </p>
                    <p className="italic text-sm leading-1 mt-2 mb-4">
                      One streamlined platform for your entire clinic.
                    </p>
                    <img
                      alt=""
                      src="https://images.squarespace-cdn.com/content/v1/615dc239b586c31b30e953a1/42ea02e1-820f-4092-a91d-c449570981ad/Dashboard-view-01.gif?format=500w"
                    ></img>
                    <p className="text-sm leading-1 mt-4 mb-4">
                      Get started - it's free. No credit card required.
                    </p>
                    <form
                      className="h-full"
                      onSubmit={registerSubmitStepOne(stepForward, {
                        onFail: onFailStepOne,
                      })}
                    >
                      <Input
                        label="Email*"
                        name="email"
                        onChange={onChangeStepOne('email')}
                        value={valueOne.email}
                        errors={errorsStepOne.fieldErrors?.email}
                        className="flex-grow mb-2"
                      />
                      {valueOne.email && emailExists && (
                        <Alert className="mt-2" icon={AlertIcons.info}>
                          It looks like you already have an account with
                          ChiroUp.{' '}
                          <Link
                            to="/"
                            className="font-medium underline text-blue-700 hover:text-blue-600"
                          >
                            Click here to login.
                          </Link>
                        </Alert>
                      )}
                      {notValidEmail && <ValidEmailAlert styles={'mt-2'} />}
                      <Input
                        label="First name*"
                        name="fname"
                        onChange={onChangeStepOne('fname')}
                        value={valueOne.fname}
                        errors={errorsStepOne.fieldErrors?.fname}
                        className="flex-grow mb-2"
                      />

                      <Input
                        label="Last name*"
                        name="lname"
                        onChange={onChangeStepOne('lname')}
                        value={valueOne.lname}
                        errors={errorsStepOne.fieldErrors?.lname}
                        className="flex-grow mb-2"
                      />
                      <div className="text-center">
                        <Button
                          text="Continue"
                          type="submit"
                          color={ButtonColors.accent}
                          className="mt-4 text-xl mb-5"
                          disabled={isCheckingEmail || submittingStepOne}
                          loading={isCheckingEmail || submittingStepOne}
                        />
                      </div>
                      <p className="text-center text-sm text-gray-600 pb-10">
                        Already have an account?
                        <Link
                          to="/"
                          className="font-medium text-primary-500 hover:text-primary-300"
                        >
                          {' '}
                          Log in
                        </Link>
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
            <div
              className="box-border border-primary-100 bg-gray-300 rounded-l-md w-1/3 flex-col lg:space-y-16 space-y-12 sm:block hidden"
              style={{ marginLeft: '1px' }}
            >
              <div className="center">
                <Logo className="mt-5 ml-auto mr-auto logo-light" />
              </div>
              <div className="w-100">
                <p className="italic text-center ml-5 mr-5 md:leading-7 xs:text-md md:text-lg lg:text-xl text-gray-700">
                  "The greatest addition for patient education, retention, and
                  compliance."
                </p>
              </div>
              <p className="text-md text-center ml-5 mr-5 leading-1 lg:leading-5 text-gray-700">
                Trusted by over 2,500 chiropractors around the world.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
